import React from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin } from "lucide-react";
import StarRating from "./StarRating";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const typeLabel = (t) => (t === "cafe" ? "Café" : t === "bean_shop" ? "Bean Shop" : "Café · Bean Shop");

export default function CafeCard({ cafe, layout = "default" }) {
  const { user, favorites, toggleFavorite } = useAuth();
  const isFav = favorites?.has(cafe.id);

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Sign in to save favorites", { description: "It only takes a second." });
      return;
    }
    const r = await toggleFavorite(cafe.id);
    if (r.ok) toast(r.favorited ? "Added to favorites" : "Removed from favorites");
  };

  return (
    <Link
      to={`/cafe/${cafe.id}`}
      data-testid={`cafe-card-${cafe.id}`}
      className="cafe-card group block bg-card border border-border overflow-hidden rounded-sm hover:-translate-y-1 transition-transform"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#E8DFD3]">
        {cafe.image_url && (
          <img
            src={cafe.image_url}
            alt={cafe.name}
            loading="lazy"
            className="cafe-card-image w-full h-full object-cover"
          />
        )}
        <button
          onClick={handleFav}
          data-testid={`favorite-button-${cafe.id}`}
          aria-label={isFav ? "Remove favorite" : "Add favorite"}
          className="absolute top-3 right-3 h-9 w-9 grid place-items-center bg-[#F9F6F0] border border-border rounded-full hover:bg-[#F2EBE1] transition-colors"
        >
          <Heart
            className={isFav ? "fill-[#C05A46] text-[#C05A46]" : "text-[#2C1E16]"}
            size={16}
            strokeWidth={1.5}
          />
        </button>
        <div className="absolute bottom-3 left-3">
          <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[#F9F6F0] border border-border rounded-full text-[#786B63]">
            {typeLabel(cafe.type)}
          </span>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-serif text-2xl leading-tight text-[#2C1E16]">{cafe.name}</h3>
          <span className="text-xs text-[#786B63] mt-1.5">{cafe.price_range || "$$"}</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-[#786B63]">
          <MapPin size={12} strokeWidth={1.5} />
          <span>{cafe.city}</span>
        </div>
        <p className="mt-3 text-sm text-[#786B63] line-clamp-2 leading-relaxed">
          {cafe.description}
        </p>
        <div className="mt-4 flex items-center justify-between">
          <StarRating rating={cafe.rating_avg} showValue />
          <span className="text-xs text-[#786B63]">
            {cafe.review_count || 0} review{(cafe.review_count || 0) === 1 ? "" : "s"}
          </span>
        </div>
      </div>
    </Link>
  );
}
